import { useCallback, useMemo } from 'react';

import { useAuthentication } from '@zenbusiness/application-commons-authentication';
import { useMutation } from '@zenbusiness/application-commons-graphql';
import { useCaptureException } from '@zenbusiness/application-commons-logging';

import { useUnifiedBusinessEntityContext } from '#client/contexts/UnifiedBusinessEntityProvider';

import { UPDATE_THREAD_TITLE_MUTATION } from '../mutations/updateThreadTitle';

/**
 * Custom hook for updating thread titles
 *
 * Handles:
 * - Loading states
 * - Error handling
 * - Authentication via GraphQL
 *
 * @returns {object} Hook state and methods
 */
export function useUpdateThreadTitle() {
  const { businessEntityUuid } = useUnifiedBusinessEntityContext();
  const { userUuid } = useAuthentication();

  const captureException = useCaptureException({
    tags: {
      frontend: 'assistant-ui',
      operationName: 'updateThreadTitle'
    }
  });

  // Memoize identity to prevent unnecessary query re-executions
  const identity = useMemo(
    () => ({
      customer: {
        userUuid,
        ...(businessEntityUuid && { businessEntityUuid })
      }
    }),
    [userUuid, businessEntityUuid]
  );

  const [updateThreadTitleMutation, { loading }] = useMutation(UPDATE_THREAD_TITLE_MUTATION, {
    onError: (err) => {
      captureException(err, {
        tags: { errorType: 'graphql_mutation' },
        contexts: {
          identity: { businessEntityUuid, userUuid },
          mutationError: {
            message: err.message,
            networkError: err.networkError,
            graphQLErrors: err.graphQLErrors
          }
        }
      });
    }
  });

  /**
   * Updates a thread's title
   * Note: Requires authentication - only authenticated users can edit thread titles
   * @param {string} threadUuid - The UUID of the thread to update
   * @param {string} newTitle - The new title
   * @returns {Promise<object>} The updated thread data
   */
  const updateThreadTitle = useCallback(
    async (threadUuid, newTitle) => {
      try {
        const response = await updateThreadTitleMutation({
          variables: {
            identity,
            threadUuid,
            title: newTitle
          }
        });

        const responseData = response?.data?.updateThreadTitle;

        // Check for response-level errors
        if (!responseData?.ok || responseData?.error) {
          const errorMessage =
            typeof responseData?.error === 'string'
              ? responseData.error
              : 'Failed to update thread title';
          throw new Error(errorMessage);
        }

        return responseData;
      } catch (err) {
        captureException(err, {
          tags: { errorType: 'response_error', threadUuid },
          contexts: {
            request: {
              threadUuid,
              titleLength: newTitle?.length,
              userUuid,
              businessEntityUuid
            }
          }
        });

        throw err;
      }
    },
    [updateThreadTitleMutation, identity, userUuid, businessEntityUuid, captureException]
  );

  return {
    updateThreadTitle,
    loading
  };
}
